/**
 * chromium-pool public surface + the default-off singleton gate.
 *
 * **Disabled by default.** browser-core ships the Chromium pool off; a host opts in
 * via {@link configureChromiumPool} (a knob-backed gate, like the ffmpeg pool).
 * While the gate is off the pool is never constructed and
 * {@link getChromiumPoolStats} returns `null`, so a host that leaves it alone is
 * byte-identical to having no pool at all.
 *
 * The apps/api host wires the gate to the `chromium.pool.enabled` knob and routes a
 * `usesBrowser` job's browser here for orgs carrying the `browser.local-pool` flag
 * (`apps/api/src/chromium-pool/config.ts`, `apps/api/src/browser-pool/routing-pool.ts`).
 * Enabled on dev; production still routes every org to the in-process pool.
 *
 * @module chromium-pool
 */

import { cpus } from 'node:os';
import { type BrowserLogger, consoleLogger } from '../logger.js';
import {
  ChromiumPool,
  type ChromiumLease,
  type ChromiumLeaseOptions,
  type ChromiumPoolStats,
  ChromiumWorkerError,
} from './pool.js';

export {
  ChromiumPool,
  ChromiumWorkerError,
  resolveChromiumRunnerPath,
  type ChromiumLease,
  type ChromiumLeaseOptions,
  type ChromiumPoolOptions,
  type ChromiumPoolStats,
} from './pool.js';

/** Default lease TTL (ms) — the window a holder has to `heartbeat()` before reclaim. */
export const DEFAULT_CHROMIUM_LEASE_TTL_MS = 60_000;

/**
 * Fallback runner count when the host doesn't pass `size`. The real bound is the
 * derived per-worker browser-slot count `N` (spec §4.0); this is only a
 * last-resort default.
 *
 * ⚠️ `os.cpus()` reports the HOST's cores, not the container's CPU limit — never
 * the production source; prefer the host-passed size.
 */
const defaultChromiumPoolSize = (): number => Math.min(4, Math.max(1, Math.floor(cpus().length / 4)));

export interface ChromiumPoolConfig {
  /** Master gate. `boolean` or a function so the host can back it with a live knob. */
  enabled: boolean | (() => boolean);
  /** Browser count. Applied when the pool is first created. */
  size?: number;
  /** Recycle threshold (bytes): a browser over this RSS is recycled on release. */
  rssRecycleBytes?: number;
  /** Default lease TTL (ms). */
  leaseTtlMs?: number;
  /**
   * Arm the per-driver CPU/PSS calibration sampler at this poll cadence (ms) for
   * leased browsers. Unset/`0` ⇒ off (default). Applied **live** via
   * {@link configureChromiumPool} — starts/stops sampling on a warm pool.
   */
  measureResourcesMs?: number;
  logger?: BrowserLogger;
}

let config: ChromiumPoolConfig = { enabled: false };
let pool: ChromiumPool | null = null;

/**
 * Configure (and enable/disable) the Chromium pool. Safe to call repeatedly — a
 * literal `enabled: false` tears the pool down; a function gate leaves teardown to
 * the host via {@link shutdownChromiumPool}.
 */
export function configureChromiumPool(next: ChromiumPoolConfig): void {
  config = next;
  if (typeof next.enabled === 'boolean' && next.enabled === false) {
    void shutdownChromiumPool();
    return;
  }
  // Apply the calibration cadence live (idempotent; no-op until the pool is built).
  pool?.setMeasureResourcesMs(config.measureResourcesMs ?? 0);
}

export function isChromiumPoolEnabled(): boolean {
  const { enabled } = config;
  return typeof enabled === 'function' ? !!enabled() : enabled;
}

function getPool(): ChromiumPool {
  if (!pool) {
    pool = new ChromiumPool({
      size: config.size ?? defaultChromiumPoolSize(),
      defaultLeaseTtlMs: config.leaseTtlMs ?? DEFAULT_CHROMIUM_LEASE_TTL_MS,
      ...(config.rssRecycleBytes ? { rssRecycleBytes: config.rssRecycleBytes } : {}),
      ...(config.measureResourcesMs ? { measureResourcesMs: config.measureResourcesMs } : {}),
      logger: config.logger ?? consoleLogger,
    });
  }
  return pool;
}

export async function shutdownChromiumPool(): Promise<void> {
  const p = pool;
  pool = null;
  if (p) await p.shutdown();
}

/**
 * Current pool stats, or `null` when the pool isn't built (disabled / torn down).
 * The host's telemetry loop reads this each interval and emits per-driver metrics.
 */
export function getChromiumPoolStats(): ChromiumPoolStats | null {
  return pool ? pool.stats() : null;
}

/**
 * Lease one browser from the configured pool — the consumer entrypoint (slice 1
 * shipped the gate dark with no leaser). The host's browser-pool backend calls
 * this per `acquire()` and connects `chromium.connectOverCDP(lease.cdpEndpoint)`.
 *
 * Throws {@link ChromiumWorkerError} when the pool is disabled, so a routed
 * acquire surfaces a pool fault (the host maps it to its exhaustion/backpressure
 * path) rather than silently leasing from a torn-down pool.
 */
export async function leaseChromiumBrowser(opts?: ChromiumLeaseOptions): Promise<ChromiumLease> {
  if (!isChromiumPoolEnabled()) {
    throw new ChromiumWorkerError('chromium pool is disabled — refusing to lease');
  }
  return getPool().lease(opts);
}

/** @internal test seam — also reclaims any live runner processes. */
export async function _resetChromiumPoolForTest(): Promise<void> {
  const p = pool;
  config = { enabled: false };
  pool = null;
  if (p) await p.shutdown();
}

/** How long the smoke-test waits for the DevTools server to answer `/json/version`. */
const SMOKE_TEST_PROBE_MS = 5_000;

/**
 * Boot smoke-test: spawn one runner, launch its Chromium, lease it, and probe the
 * leased CDP endpoint (`GET /json/version`) — proving end-to-end that the runner
 * SPAWNS, Chromium LAUNCHES, and its DevTools server actually ACCEPTS a
 * connection (not just that a well-formed endpoint string came back). Throws on
 * any failure so the host logs a loud boot failure instead of silently degrading.
 * Call AFTER {@link configureChromiumPool}.
 */
export async function smokeTestChromiumPool(): Promise<void> {
  const lease = await getPool().lease();
  try {
    if (!lease.cdpEndpoint.startsWith('http')) {
      throw new ChromiumWorkerError(
        `chromium smoke-test got a non-http CDP endpoint: ${lease.cdpEndpoint}`
      );
    }
    let res: Response;
    try {
      res = await fetch(`${lease.cdpEndpoint}/json/version`, {
        signal: AbortSignal.timeout(SMOKE_TEST_PROBE_MS),
      });
    } catch (err) {
      throw new ChromiumWorkerError(
        `chromium smoke-test could not reach DevTools at ${lease.cdpEndpoint}: ${
          err instanceof Error ? err.message : String(err)
        }`
      );
    }
    if (!res.ok) {
      throw new ChromiumWorkerError(
        `chromium smoke-test DevTools probe returned HTTP ${res.status}`
      );
    }
  } finally {
    await lease.release();
  }
}
